// src/pages/communication/schedule/ScheduleCalendar.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Calendar, {
  buildStatusCounts,
  getKRHolidayMap,
} from "../../../components/common/feature/Calendar";
import BoardTable from "../../../components/common/board/BoardTable";
import SidePanel from "../../../components/common/layout/SidePanel";
import PageHeader from "../../../components/common/ui/PageHeader";
import { format } from "date-fns";
import { scheduleApi } from "../../../api/schedules";
import api from "../../../api/axios"; // added for fetching company info
// Removed unused imports for membership and PCP
import { fetchMembership } from "../../../api/users/membership";
import ScheduleDetail from "./ScheduleDetail";
import ScheduleNew from "./ScheduleNew";
import { useParams } from "react-router-dom";

export default function ScheduleCalendar() {
  // --- 소속 회사 로드 ---
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchMembership();
        const data = res.data;
        const arr = data?.memberships ?? data?.results ?? (Array.isArray(data) ? data : []);
        const primary = arr.find((m) => m.is_primary) ?? arr[0] ?? null;
        console.log("🏢 [DEBUG] Company ID Loaded:", primary?.company);
        setCompanyId(primary?.company ?? null);
      } catch (e) {
        console.error("company load error", e);
      }
    })();
  }, []);

  const { id: projectId } = useParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const [selectedScopes, setSelectedScopes] = useState([
    "project",
    "company",
    "personal",
  ]);

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);

  const [companyId, setCompanyId] = useState(null);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  // Company loading removed per user request

  const toggleScope = (type) => {
    setSelectedScopes((prev) =>
      prev.includes(type) ? prev.filter((v) => v !== type) : [...prev, type]
    );
  };

  const clearAll = () => setSelectedScopes([]);

  // --- 일정 로드 ---
  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      let list = [];

      if (selectedScopes.includes("project") && projectId) {
        const res = await scheduleApi.listProject(projectId);
        list = [...list, ...(res.data?.results ?? res.data ?? [])];
      }

      if (selectedScopes.includes("company") && companyId) {
        const res = await scheduleApi.listCompany(companyId);
        list = [...list, ...(res.data?.results ?? res.data ?? [])];
      }

      if (selectedScopes.includes("personal")) {
        const res = await scheduleApi.listPersonal();
        list = [...list, ...(res.data?.results ?? res.data ?? [])];
      }

      setEvents(list);
      console.log("📌 events result:", list);
    } catch (err) {
      console.error("fetchSchedules failed:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [selectedScopes, projectId, companyId]);

  useEffect(() => {
    fetchSchedules();
  }, [selectedScopes, fetchSchedules]);

  // 리스트 필터링
  const list = useMemo(
    () => events.filter((e) => (e.start ?? "").slice(0, 10) === dateStr),
    [events, dateStr]
  );

  // 상태 dot 구성
  const statusCountsByDate = useMemo(
    () =>
      buildStatusCounts(
        events,
        (m) => (m.start ?? "").slice(0, 10),
        (m) =>
          m.scope === "personal"
            ? "green"
            : m.scope === "company"
              ? "red"
              : "blue"
      ),
    [events]
  );

  console.log("📌 statusCountsByDate:", statusCountsByDate);

  const [holidayMap, setHolidayMap] = useState({});
  useEffect(() => {
    (async () =>
      setHolidayMap(await getKRHolidayMap(selectedDate.getFullYear())))();
  }, [selectedDate]);

  return (
    <div className="p-6 space-y-4">

      {/* ============================
          PAGE HEADER
      ============================ */}
      <PageHeader>일정 관리</PageHeader>

      {/* ============================
          FILTER BUTTONS
      ============================ */}
      

      {/* ============================
          REGISTER BUTTON
      ============================ */}
      <div className="flex justify-between">
        <div className="flex flex-wrap gap-2">
        {[
          { key: "project", label: "현장", color: "blue" },
          { key: "company", label: "참여사", color: "red" },
          { key: "personal", label: "개인", color: "green" },
        ].map((opt) => {
          const active = selectedScopes.includes(opt.key);
          return (
            <button
              key={opt.key}
              onClick={() => toggleScope(opt.key)}
              className={`
          px-3 py-1.5 rounded-full border text-sm
          transition-all

          ${active
                  ? `bg-${opt.color}-100 text-${opt.color}-700 border-${opt.color}-500`
                  : "bg-white text-slate-700 hover:bg-slate-100"
                }
        `}
            >
              {opt.label}
            </button>
          );
        })}

        <button onClick={clearAll} className="btn-basic px-3 py-1.5">
          전체 해제
        </button>
      </div>

        <button
          className="btn-create"
          onClick={() => {
            setPanelMode("create");
            setPanelOpen(true);
          }}
        >
          일정 등록
        </button>
      </div>

      {/* ============================
          MAIN CONTENT: Calendar + List
      ============================ */}
      <div className="flex gap-6 items-start">

        {/* CALENDAR CARD */}
        <div className="w-[550px] border rounded-xl bg-white shadow-sm p-4">
          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            statusCountsByDate={statusCountsByDate}
            holidayMap={holidayMap}
            showCounts={true}
          />
        </div>

        {/* LIST CARD */}
        <div className="flex-1 border rounded-xl bg-white shadow-sm p-4 max-h-[80vh] overflow-y-auto">
          <h3 className="text-base font-semibold mb-4">{dateStr} 일정</h3>

          <BoardTable
            keyField="id"
            rows={list}
            loading={loading}
            emptyText="일정이 없습니다."
            columns={[
              {
                key: "time",
                header: "시간",
                render: (row) => format(new Date(row.start), "HH:mm"),
              },
              {
                key: "title",
                header: "제목",
                render: (row) => (
                  <div className="flex items-center gap-2">
                    <span className="truncate">{row.title}</span>

                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        row.scope === "personal"
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : row.scope === "company"
                            ? "bg-red-50 text-red-700 border border-red-200"
                            : "bg-blue-50 text-blue-700 border border-blue-200"
                        }`}
                    >
                      {row.scope === "personal"
                        ? "개인"
                        : row.scope === "company"
                          ? "참여사"
                          : "현장"}
                    </span>
                  </div>
                ),
              },
            ]}
            onRowClick={(item) => {
              setActiveItem(item);
              setPanelMode("view");
              setPanelOpen(true);
            }}
          />
        </div>
      </div>

      {/* ============================
          SIDE PANEL
      ============================ */}
      <SidePanel isOpen={panelOpen} lockScroll={false} onClose={() => setPanelOpen(false)} width={500}>
        {panelMode === "create" && (
          <ScheduleNew
            projectId={projectId}
            companyId={companyId}
            // 🔥 [수정] 현재 필터 상태에 따라 기본 Scope 적절히 선택 (예: 회사만 보고 있으면 회사 일정 등록)
            defaultScope={
               selectedScopes.length === 1 && selectedScopes[0] === "company" 
               ? "company" 
               : (selectedScopes.includes("project") ? "project" : "personal")
            }
            onSaved={() => {
              fetchSchedules();
              setPanelOpen(false);
            }}
            onClose={() => setPanelOpen(false)}
          />
        )}

        {panelMode === "view" && activeItem && (
          <ScheduleDetail
            item={activeItem}
            onEdit={() => setPanelMode("edit")}
            onDeleted={() => {
              fetchSchedules();
              setPanelOpen(false);
            }}
          />
        )}

        {panelMode === "edit" && activeItem && (
          <ScheduleNew
            initial={activeItem}
            mode="edit"
            projectId={projectId}
            companyId={companyId}
            scheduleType={activeItem.scope}
            onSaved={() => {
              fetchSchedules();
              setPanelOpen(false);
            }}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </SidePanel>
    </div>
  );
}
